/* MUI */
import { Check, FiberManualRecord } from '@mui/icons-material';
import { Box, Hidden, TableCell, TableRow, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/* Shared */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { Sources, UserBubbles } from 'application/Shared/Components/Avatars';
import {
    LifecyclePicker,
    RiskPicker
} from 'application/Shared/Components/Pickers';
import { ManagedTable } from 'application/Shared/Components/Table';
import { TagLinksList } from 'application/Shared/Components/Tag';
import { FontSizes } from 'application/Shared/Constants';
import { Helpers } from 'application/Shared/Helpers';
import Core from 'application/Shared/Helpers/Functions';

/* Objects */
import { Application, ApplicationBusinessOwner } from 'objects/Application';

/* React */
import React, { useMemo } from 'react';
import { Trans, withTranslation } from 'react-i18next';
import { Sparklines, SparklinesBars, SparklinesLine } from 'react-sparklines';

const MicrosoftOfficeOverlapReports = (props) => {
    const { reportData, reportSetup } = props;
    const {
        microsoftOfficeOverlapMicrosoftOfficeOverlapOverview,
        microsoftOfficeOverlapMicrosoftOfficeOverlapOverviewBreakdown,
        microsoftOfficeOverlapMicrosoftOfficeOverlapNoOverlapBreakdown
    } = reportData || {};

    return (
        <Box
            width="100%"
            id="microsoftOfficeOverlapMicrosoftOfficeOverlapOverview"
        >
            {microsoftOfficeOverlapMicrosoftOfficeOverlapOverview && (
                <DiscoveryOverview
                    {...props}
                    reportSetup={reportSetup}
                    reportData={
                        microsoftOfficeOverlapMicrosoftOfficeOverlapOverview
                    }
                />
            )}
            {microsoftOfficeOverlapMicrosoftOfficeOverlapOverviewBreakdown && (
                <Box
                    id="microsoftOfficeOverlapMicrosoftOfficeOverlapOverviewBreakdown"
                    width="100%"
                >
                    <DiscoveryOverviewBreakdown
                        {...props}
                        reportSetup={reportSetup}
                        reportData={
                            microsoftOfficeOverlapMicrosoftOfficeOverlapOverviewBreakdown
                        }
                    />
                </Box>
            )}
            {microsoftOfficeOverlapMicrosoftOfficeOverlapNoOverlapBreakdown && (
                <Box
                    id="microsoftOfficeOverlapMicrosoftOfficeOverlapNoOverlapBreakdown"
                    width="100%"
                >
                    <DiscoveryApplicationBreakdown
                        {...props}
                        reportSetup={reportSetup}
                        reportData={
                            microsoftOfficeOverlapMicrosoftOfficeOverlapNoOverlapBreakdown
                        }
                    />
                </Box>
            )}
        </Box>
    );
};

const headersOverview = (t) => [
    {
        align: 'left',
        field: 'riubApplication',
        renderHeader: <strong>{t('Common.application')}</strong>,
        width: '35%'
    },
    {
        align: 'left',
        field: 'riubApplication',
        renderHeader: <strong>{t('Common.categories')}</strong>,
        width: '35%'
    },
    {
        align: 'center',
        field: 'riubUsed',
        renderHeader: <strong>{t('Common.currently_used')}</strong>,
        width: '15%'
    },
    {
        align: 'center',
        field: 'overlapApps',
        renderHeader: <strong>{t('Common.overlap_apps')}</strong>,
        width: '15%'
    }
];

const RenderRowOverview = ({ row }) => (
    <TableRow
        sx={{
            backgroundColor: 'table.rowBackground',
            overflow: 'hidden'
        }}
        key={`table_row_iub_app_${row.microsoftOfficeApp.applicationId}`}
    >
        <TableCell>
            <Application
                key={`table_cell_iub_app_${row.microsoftOfficeApp.applicationId}`}
                data={row.microsoftOfficeApp}
                nameHasLink={!!row.microsoftOfficeAppTenantLink}
            >
                <ApplicationNameLinkCell />
            </Application>
        </TableCell>
        <TableCell>
            {row.microsoftOfficeApp.categories.map((category) => (
                <Box
                    key={`catBox1_${row.microsoftOfficeApp.applicationId}_${category}`}
                >
                    {category}
                </Box>
            ))}
        </TableCell>
        <TableCell
            sx={{
                backgroundColor: row.microsoftOfficeAppTenantLink
                    ? 'table.successRowBackground'
                    : 'table.dangerRowBackground',
                color: row.microsoftOfficeAppTenantLink
                    ? 'table.successRowText'
                    : 'table.dangerRowText',
                textAlign: 'center'
            }}
        >
            {row.microsoftOfficeAppTenantLink ? (
                <Check />
            ) : (
                <FiberManualRecord style={{ fontSize: FontSizes[0] }} />
            )}
        </TableCell>
        <TableCell align="center">{row.overlapAppsCount}</TableCell>
    </TableRow>
);

const DiscoveryOverview = (props) => {
    const { reportData, t } = props;

    const rows = Object.values(reportData.data);

    const tableHeadersOverview = useMemo(() => headersOverview(t), [t]);

    return (
        <Box marginY="32px">
            <Typography variant="h1">
                {t('Reports.microsoftOfficeOverlapReport.overview.title')}
            </Typography>
            <Box>
                <Typography paragraph mt="16px">
                    {t(
                        'Reports.microsoftOfficeOverlapReport.overview.description'
                    )}
                </Typography>
                <Typography paragraph>
                    <Trans
                        i18nKey="Reports.microsoftOfficeOverlapReport.currentlyUsed"
                        components={{
                            MakeItBold: (
                                <Typography variant="span" fontWeight={600} />
                            )
                        }}
                    />
                </Typography>
            </Box>
            <hr />
            <ManagedTable
                className="squeezedTable"
                headers={tableHeadersOverview}
                manageRows
                renderBody={(row) => <RenderRowOverview row={row} />}
                renderBodyKey={null}
                renderFooter={false}
                rows={rows}
                rowsPerPage={-1}
                searchEnabled={false}
                sortDirection="DESC"
                sortFieldType="number"
                sortKey="usage"
            />
        </Box>
    );
};

const headersOverviewBreakdown = (t) => [
    {
        align: 'left',
        field: 'riubApplication',
        renderHeader: <strong>{t('Common.application')}</strong>,
        width: '30%'
    },
    {
        align: 'left',
        field: 'risk',
        renderHeader: <strong>{t('Common.risk')}</strong>,
        sortableKey: 'application.risk',
        tooltip: 'risk',
        width: '15%'
    },
    {
        align: 'left',
        field: 'overviewCategory',
        renderHeader: <strong>{t('Common.users')}</strong>,
        width: '15%'
    },
    {
        align: 'left',
        field: 'source',
        renderHeader: <strong>{t('Common.source')}</strong>,
        tooltip: 'unclassified_source',
        width: '20%'
    },
    {
        align: 'left',
        field: 'usage',
        renderHeader: <strong>{t('Common.usage_chart')}</strong>,
        sortableKey: 'usage',
        tooltip: 'usage_chart',
        width: '20%'
    }
];

const RenderRow = ({ max, row }) => {
    const theme = useTheme();
    const { users } = row;
    const sources = Helpers.getSources(row);

    return (
        <Application
            key={`renderBody_iub_app_${row.applicationId}`}
            data={row.application}
            nameHasLink
        >
            <TableCell>
                <Box>
                    <ApplicationNameLinkCell nameNoTruncate withCategory />
                </Box>
            </TableCell>
            <TableCell>
                <RiskPicker visualOnly value={row.application.risk} />
            </TableCell>
            <Hidden lgUp>
                <TableCell align="center">{row.users.length}</TableCell>
            </Hidden>
            <Hidden lgDown>
                <TableCell align="center">
                    <UserBubbles
                        align="right"
                        max={5}
                        tooltipPlacement="right"
                        users={users}
                    />
                </TableCell>
            </Hidden>
            <TableCell>
                <Sources sources={sources} max={3} />
            </TableCell>
            <TableCell>
                {row.usageChart && (
                    <div
                        style={{
                            width: '150px'
                        }}
                    >
                        <Sparklines
                            max={max}
                            data={Core.padArray(row.usageChart, 20, 0)}
                        >
                            <SparklinesBars
                                style={{
                                    fill: theme.palette.sparkLines.sparkBar,
                                    stroke: theme.palette.sparkLines.stroke
                                }}
                            />
                            <SparklinesLine
                                color={theme.palette.sparkLines.sparkLine}
                                style={{
                                    fill: 'none'
                                }}
                            />
                        </Sparklines>
                    </div>
                )}
            </TableCell>
        </Application>
    );
};

const DiscoveryOverviewBreakdown = (props) => {
    const { max, reportData, t } = props;

    const tableHeadersOverviewBreakdown = useMemo(
        () => headersOverviewBreakdown(t),
        [t]
    );

    return (
        <Box marginY="32px">
            <Typography variant="h1">
                {t(
                    'Reports.microsoftOfficeOverlapReport.overviewBreakdown.title'
                )}
            </Typography>
            <Typography paragraph mt="16px">
                {t(
                    'Reports.microsoftOfficeOverlapReport.overviewBreakdown.description'
                )}
            </Typography>
            <hr />

            {reportData.data.map((app, index) => (
                <Box
                    className="noBreak"
                    p="8px"
                    sx={{
                        backgroundColor:
                            index % 2 === 0
                                ? 'table.evenRowBackground'
                                : 'table.oddRowBackground'
                    }}
                    key={`iub_app_wrapper_${app.microsoftOfficeApp.applicationId}`}
                >
                    {app.microsoftOfficeAppTenantLink === null ? (
                        <Application
                            key={`render_1_iub_app_${app.microsoftOfficeApp.applicationId}}`}
                            data={app.microsoftOfficeApp}
                        >
                            <Box>
                                <Box display="flex" alignItems="flex-end">
                                    <Typography
                                        component="span"
                                        style={{
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        <ApplicationNameLinkCell nameClassname="bold" />
                                    </Typography>
                                    <Typography
                                        color="text.danger"
                                        style={{
                                            marginLeft: '4px',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        (Not Currently Used)
                                    </Typography>
                                </Box>
                                {app.microsoftOfficeApp.categories.map(
                                    (category) => (
                                        <Box
                                            ml="20px"
                                            key={`catBox2_${app.microsoftOfficeApp.applicationId}_${category}`}
                                        >
                                            <small>{category}</small>
                                        </Box>
                                    )
                                )}
                            </Box>

                            <Box ml="20px" mt="8px">
                                <Typography style={{ fontWeight: 600 }}>
                                    Business Purpose:
                                </Typography>
                                {app.microsoftOfficeApp.purpose ? (
                                    <div>{app.microsoftOfficeApp.purpose}</div>
                                ) : (
                                    <Typography color="text.danger">
                                        No Purpose Set
                                    </Typography>
                                )}
                            </Box>
                            <Box ml="20px" mt="8px">
                                <Typography style={{ fontWeight: 600 }}>
                                    Risk:
                                </Typography>
                                <RiskPicker
                                    visualOnly
                                    value={app.microsoftOfficeApp.risk}
                                />
                            </Box>
                        </Application>
                    ) : (
                        <Application
                            key={`render_2_iub_app_${app.microsoftOfficeApp.applicationId}}`}
                            data={app.microsoftOfficeAppTenantLink.application}
                            nameHasLink
                        >
                            <Box>
                                <Typography
                                    variant="h4"
                                    style={{ marginBottom: 0 }}
                                >
                                    <ApplicationNameLinkCell />
                                </Typography>
                                {app.microsoftOfficeApp.categories.map(
                                    (category) => (
                                        <Box
                                            ml="20px"
                                            key={`catBox3_${app.microsoftOfficeApp.applicationId}_${category}`}
                                        >
                                            <small>{category}</small>
                                        </Box>
                                    )
                                )}
                            </Box>
                            <Box pl="20px" width="100%">
                                <table>
                                    <tr>
                                        <td width="33%">
                                            <strong>Business Owner: </strong>
                                            <br />
                                            <ApplicationBusinessOwner
                                                businessOwnerPlatformUserId={
                                                    app
                                                        .microsoftOfficeAppTenantLink
                                                        .businessOwnerPlatformUserId
                                                }
                                                noBusinessOwnerRequired={
                                                    app
                                                        .microsoftOfficeAppTenantLink
                                                        .noBusinessOwnerRequired
                                                }
                                            />
                                        </td>
                                        <td width="33%">
                                            <strong>Tags: </strong>
                                            <br />
                                            <TagLinksList
                                                tagLinks={
                                                    app
                                                        .microsoftOfficeAppTenantLink
                                                        ?.tagLinks
                                                }
                                            />
                                        </td>
                                        <td width="33%">
                                            <strong>Lifecycle Stage: </strong>
                                            <br />
                                            <LifecyclePicker
                                                visualOnly
                                                value={
                                                    app
                                                        .microsoftOfficeAppTenantLink
                                                        .status
                                                }
                                            />
                                        </td>
                                    </tr>
                                </table>

                                {!!app.microsoftOfficeAppTenantLink.application
                                    .risk && (
                                    <>
                                        <strong>Risk: </strong>
                                        <br />
                                        <RiskPicker
                                            visualOnly
                                            value={
                                                app.microsoftOfficeAppTenantLink
                                                    .application.risk
                                            }
                                        />
                                        <br />
                                    </>
                                )}
                                <br />
                                <strong>Business Purpose: </strong>
                                <br />
                                {app.microsoftOfficeAppTenantLink.purpose ? (
                                    <div>
                                        {
                                            app.microsoftOfficeAppTenantLink
                                                .purpose
                                        }
                                    </div>
                                ) : (
                                    <Typography color="text.danger">
                                        No Purpose Set
                                    </Typography>
                                )}
                            </Box>
                        </Application>
                    )}

                    {/* Table for Overlap Applications */}

                    {app.overlapAppTenantLinks.length > 0 ? (
                        <Box my="32px">
                            <Typography variant="h5">
                                Potentially Overlapping Applications
                            </Typography>
                            <ManagedTable
                                headers={tableHeadersOverviewBreakdown}
                                renderBody={(row) => (
                                    <RenderRow max={max} row={row} />
                                )}
                                renderBodyKey={null}
                                renderFooter={false}
                                rows={app.overlapAppTenantLinks}
                                rowsPerPage={-1}
                                searchEnabled={false}
                                sortDirection="DESC"
                                sortFieldType="number"
                                sortKey="usage"
                            />
                        </Box>
                    ) : (
                        <Typography
                            variant="h5"
                            style={{
                                marginBottom: '32px',
                                marginTop: '32px'
                            }}
                        >
                            No Overlapping Applications Found
                        </Typography>
                    )}
                </Box>
            ))}
        </Box>
    );
};

const DiscoveryApplicationBreakdown = (props) => {
    const { reportData, t } = props;

    return (
        <Box paddingY="32px" width="100%">
            <Typography variant="h1">
                {t(
                    'Reports.microsoftOfficeOverlapReport.noOverlapOverviewBreakdown.title'
                )}
            </Typography>
            <Typography paragraph mt="16px">
                {t(
                    'Reports.microsoftOfficeOverlapReport.noOverlapOverviewBreakdown.description'
                )}
            </Typography>
            <hr />

            {reportData.data.map((app, index) => (
                <Box
                    p="8px"
                    className="noBreak"
                    style={{
                        backgroundColor:
                            index % 2 === 0
                                ? 'table.evenRowBackground'
                                : 'table.oddRowBackground'
                    }}
                    key={`reportDataWrapper_iub_app_${app.microsoftOfficeApp.applicationId}`}
                >
                    {app.microsoftOfficeAppTenantLink === null ? (
                        <Application
                            key={`reportData1_iub_app_${app.microsoftOfficeApp.applicationId}`}
                            data={app.microsoftOfficeApp}
                        >
                            <Box>
                                <Box display="flex" alignItems="baseline">
                                    <Typography
                                        component="span"
                                        style={{
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        <ApplicationNameLinkCell nameClassname="bold" />
                                    </Typography>
                                    <Typography
                                        color="text.danger"
                                        style={{
                                            marginLeft: '4px',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        (Not Currently Used)
                                    </Typography>
                                </Box>
                                {app.microsoftOfficeApp.categories.map(
                                    (category) => (
                                        <Box
                                            ml="20px"
                                            key={`catBox4_${app.microsoftOfficeApp.applicationId}_${category}`}
                                        >
                                            <small>{category}</small>
                                        </Box>
                                    )
                                )}
                            </Box>

                            <Box ml="20px" mt="8px">
                                <Typography style={{ fontWeight: 600 }}>
                                    Business Purpose:
                                </Typography>
                                {app.microsoftOfficeApp.purpose ? (
                                    <div>{app.microsoftOfficeApp.purpose}</div>
                                ) : (
                                    <Typography color="text.danger">
                                        No Purpose Set
                                    </Typography>
                                )}
                            </Box>
                            <Box ml="20px" mt="8px">
                                <Typography style={{ fontWeight: 600 }}>
                                    Risk:
                                </Typography>
                                <RiskPicker
                                    visualOnly
                                    value={app.microsoftOfficeApp.risk}
                                />
                            </Box>
                        </Application>
                    ) : (
                        <Application
                            key={`reportData2_iub_app_${app.microsoftOfficeApp.applicationId}`}
                            data={app.microsoftOfficeAppTenantLink.application}
                            nameHasLink
                        >
                            <Box>
                                <Typography
                                    variant="h4"
                                    style={{ marginBottom: 0 }}
                                >
                                    <ApplicationNameLinkCell />
                                </Typography>
                                {app.microsoftOfficeApp.categories.map(
                                    (category) => (
                                        <Box
                                            ml="20px"
                                            key={`catBox5_${app.microsoftOfficeApp.applicationId}_${category}`}
                                        >
                                            <small>{category}</small>
                                        </Box>
                                    )
                                )}
                            </Box>
                            <Box pl="20px" width="100%">
                                <table>
                                    <tr>
                                        <td width="33%">
                                            <strong>Business Owner: </strong>
                                            <br />
                                            <ApplicationBusinessOwner
                                                businessOwnerPlatformUserId={
                                                    app
                                                        .microsoftOfficeAppTenantLink
                                                        .businessOwnerPlatformUserId
                                                }
                                                noBusinessOwnerRequired={
                                                    app
                                                        .microsoftOfficeAppTenantLink
                                                        .noBusinessOwnerRequired
                                                }
                                            />
                                        </td>
                                        <td width="33%">
                                            <strong>Tags: </strong>
                                            <br />
                                            <TagLinksList
                                                tagLinks={
                                                    app
                                                        .microsoftOfficeAppTenantLink
                                                        ?.tagLinks
                                                }
                                            />
                                        </td>
                                        <td width="33%">
                                            <strong>Lifecycle Stage: </strong>
                                            <br />
                                            <LifecyclePicker
                                                visualOnly
                                                value={
                                                    app
                                                        .microsoftOfficeAppTenantLink
                                                        .status
                                                }
                                            />
                                        </td>
                                    </tr>
                                </table>

                                {!!app.microsoftOfficeAppTenantLink.application
                                    .risk && (
                                    <>
                                        <strong>Risk: </strong>
                                        <br />
                                        <RiskPicker
                                            visualOnly
                                            value={
                                                app.microsoftOfficeAppTenantLink
                                                    .application.risk
                                            }
                                        />
                                        <br />
                                    </>
                                )}
                                <br />
                                <strong>Business Purpose: </strong>
                                <br />
                                {app.microsoftOfficeAppTenantLink.purpose ? (
                                    <div>
                                        {
                                            app.microsoftOfficeAppTenantLink
                                                .purpose
                                        }
                                    </div>
                                ) : (
                                    <Typography color="text.danger">
                                        No Purpose Set
                                    </Typography>
                                )}
                            </Box>
                        </Application>
                    )}
                </Box>
            ))}
        </Box>
    );
};

export default withTranslation()(MicrosoftOfficeOverlapReports);
